import { ContractAddress } from '../../constants';
import type { AccessPassNFTOptions, NFTMetadataInput } from '../nft.types';
import { buildNFTs } from '../nft.utils';

const description =
  'Offering the token holder gated and exclusive access to Mogul Productions rewards, experiences, and NFTs that are curated by the Mogul team for pass holders.';

export const RED_CARPET_ACCESS_PASS_OPTIONS: AccessPassNFTOptions = {
  contractAddress: ContractAddress.MogulNft,
  label: 'RED_CARPET_ACCESS_PASS',
  startId: 1,
  count: 25,
  tierLabel: 'Red Carpet',
  tierCode: 'RED_CARPET',
  tierIndex: 4,
  previewMediaIpfsId: 'QmX5YYoknVESBgFcXWfGivv9Q4hpkLXL7EUeiYcu3ZPjL4',
  rawMediaIpfsId: 'QmTmHMdpxm9ou5tf9j2SrngXzaBJXSDDSQphxkDMo6JDjo',
};

export const BACKSTAGE_ACCESS_PASS_OPTIONS: AccessPassNFTOptions = {
  contractAddress: ContractAddress.MogulNft,
  label: 'BACKSTAGE_ACCESS_PASS',
  startId: 26,
  count: 100,
  tierLabel: 'Backstage',
  tierCode: 'BACKSTAGE',
  tierIndex: 3,
  previewMediaIpfsId: 'QmeAwECZgXxGdbVhU6Fr1QByuV37EGBGF42D7ozy1SU4eL',
  rawMediaIpfsId: 'QmRvqvfDFKtERtTatFFCgVXT5AqDQP7jM7wNdaVPKPbkKr',
};

export const VIP_ACCESS_PASS_OPTIONS: AccessPassNFTOptions = {
  contractAddress: ContractAddress.MogulNft,
  label: 'VIP_ACCESS_PASS',
  startId: 126,
  count: 250,
  tierLabel: 'VIP',
  tierCode: 'VIP',
  tierIndex: 2,
  previewMediaIpfsId: 'QmXMKeEkEWiTCC1sidrjVLw3kYMScR36F67CGf1ThwRE3Z',
  rawMediaIpfsId: 'QmVCH4Kw1KVZfZiDoUsAwwFchnc9nXXayfykyT2mAwpEDh',
};

export const ALL_ACCESS_PASS_OPTIONS: AccessPassNFTOptions = {
  contractAddress: ContractAddress.MogulNft,
  label: 'ALL_ACCESS_PASS',
  startId: 376,
  count: 1000,
  tierLabel: 'All Access',
  tierCode: 'ALL_ACCESS',
  tierIndex: 1,
  previewMediaIpfsId: 'Qma1uPVsZfbUfUibPdb9bgSTaK38gtxibyCsBDBFcj4Zb9',
  rawMediaIpfsId: 'QmaaPCw5W8VB4cjMdvrDdRA2idfwjMFRewrcbaVqgakQ79',
};

export const GENERAL_ACCESS_PASS_OPTIONS: AccessPassNFTOptions = {
  contractAddress: ContractAddress.MogulNft,
  label: 'GENERAL_ACCESS_PASS',
  startId: 1376,
  count: 1,
  tierLabel: 'General Access',
  tierCode: 'GENERAL_ACCESS',
  tierIndex: -1,
  totalSupply: 1100,
  previewMediaIpfsId: 'Qma1uPVsZfbUfUibPdb9bgSTaK38gtxibyCsBDBFcj4Zb9',
  rawMediaIpfsId: 'QmaaPCw5W8VB4cjMdvrDdRA2idfwjMFRewrcbaVqgakQ79',
};

export const buildAccessPassNftInput = (
  issueNumber: number,
  options: AccessPassNFTOptions,
): NFTMetadataInput => {
  const properties = {
    contract_address: {
      label: 'Contract Address',
      type: 'string',
      value: options.contractAddress,
    },
    title: {
      label: 'Title',
      type: 'string',
      value: options.tierLabel.endsWith('Access')
        ? `${options.tierLabel} Pass` +
          (options.count > 1 ? ` #${issueNumber}` : '')
        : `${options.tierLabel} Access Pass` +
          (options.count > 1 ? ` #${issueNumber}` : ''),
    },
    description: {
      label: 'Description',
      type: 'string',
      value: options.tierLabel.endsWith('Access')
        ? `Mogul Productions ${options.tierLabel} Pass. ${description}`
        : `Mogul Productions ${options.tierLabel} Access Pass. ${description}`,
    },
    author: { label: 'Author', type: 'string', value: 'Mogul Productions' },
    tier_index: { label: 'Tier Index', type: 'int', value: options.tierIndex },
    tier_code: { label: 'Tier Code', type: 'string', value: options.tierCode },
    tier: {
      label: 'Tier',
      type: 'string',
      value: options.tierLabel,
    },
    external_url: {
      label: 'External URL',
      type: 'string',
      value: `https://v1.mglnft.com/${ContractAddress.MogulNft.toLowerCase()}/${
        options.startId + issueNumber - 1
      }`,
    },
    image_url: {
      label: 'Image URL',
      type: 'string',
      value: `ipfs://${options.previewMediaIpfsId}`,
    },
    animation_url: {
      label: 'Animation URL',
      type: 'string',
      value: `ipfs://${options.rawMediaIpfsId}`,
    },
    media_orientation: {
      label: 'Media Orientation',
      type: 'string',
      value: 'portrait',
    },
    background_color: {
      label: 'Background Color',
      type: 'string',
      value: '000000',
    },
    collection_label: {
      label: 'Collection',
      type: 'string',
      value: 'Mogul Access Pass',
    },
    total_supply: {
      label: 'Total Supply',
      type: 'int',
      value: options.totalSupply ?? 1,
    },
    issue_number:
      options.count > 1
        ? {
            label: 'Issue Number',
            type: 'string',
            value: `${issueNumber}/${options.count}`,
          }
        : undefined,
    issue_date: {
      label: 'Issue Date',
      type: 'date',
      value: 1618444800,
    },
  };
  return {
    title: properties.title.value,
    description: properties.description.value,
    properties,
    attributes: Object.keys(properties)
      .filter(k => ['issue_number', 'issue_date', 'tier', 'author'].includes(k))
      .map(propertyKey => properties[propertyKey as keyof typeof properties]),
  };
};

export const buildAccessPassNfts = () => {
  return [
    RED_CARPET_ACCESS_PASS_OPTIONS,
    BACKSTAGE_ACCESS_PASS_OPTIONS,
    VIP_ACCESS_PASS_OPTIONS,
    ALL_ACCESS_PASS_OPTIONS,
    GENERAL_ACCESS_PASS_OPTIONS,
  ].reduce<ReturnType<typeof buildNFTs>>((acc, options) => {
    return [
      ...acc,
      ...buildNFTs(options, (issueNumber: number) =>
        buildAccessPassNftInput(issueNumber, options),
      ),
    ];
  }, []);
};
